import type {
  ArtifactValidation,
  RunArtifact,
  Skill,
  SkillInput,
  SkillListResponse,
  RunDetailResponse,
  RunListResponse,
  RunSummary,
  RunStepRecord,
} from '@/services/studio/types';
import {
  asRecord,
  arrayOfStrings,
  humanize,
  numberOrNull,
  numberValue,
  recordValue,
  stringOrNull,
  stringValue,
} from '@/services/studio/primitives';
import { asValidationOutcome } from '@/lib/studio/validation';

export function normalizePlaybookListResponse(payload: unknown): SkillListResponse {
  const record = asRecord(payload);
  const data = asRecord(record?.data);
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(record?.playbooks)
      ? record.playbooks
      : Array.isArray(data?.playbooks)
        ? data.playbooks
        : [];
  return {
    skills: source.map((item) => normalizePlaybookToSkill(item)),
  };
}

export function normalizePlaybookDetailResponse(payload: unknown): Skill {
  const record = asRecord(payload);
  const data = asRecord(record?.data);
  return normalizePlaybookToSkill(record?.playbook ?? data?.playbook ?? data ?? payload);
}

export function normalizeArtifactRecord(input: unknown): RunArtifact {
  const raw = asRecord(input) ?? {};
  return {
    id: stringOrNull(raw.id) ?? undefined,
    name: stringValue(raw.name ?? raw.filename, "artifact"),
    content_type: stringValue(raw.content_type ?? raw.mime_type, "application/octet-stream"),
    size_bytes: numberValue(raw.size_bytes, 0),
    created_at: stringValue(raw.created_at, new Date().toISOString()),
    revision: numberOrNull(raw.revision) ?? undefined,
    validation: normalizeArtifactValidation(raw.validation),
  };
}

/**
 * Per-artifact check-only verdict. `null` for files the org can't validate (reports,
 * CSVs) — the UI shows no badge at all there. An unrecognised outcome is also dropped
 * to `null` rather than guessed at, so we never invent a verdict we didn't receive.
 */
export function normalizeArtifactValidation(input: unknown): ArtifactValidation | null {
  const raw = asRecord(input);
  if (!raw) return null;
  const outcome = asValidationOutcome(raw.outcome ?? raw.status);
  if (!outcome) return null;
  return {
    outcome,
    unit: stringOrNull(raw.unit) ?? undefined,
    metadata_type: stringOrNull(raw.metadata_type) ?? undefined,
    errors: arrayOfStrings(raw.errors),
    reason: stringOrNull(raw.reason ?? raw.unvalidated_reason),
    check_only: raw.check_only !== false,
    deployed: raw.deployed === true,
  };
}

export function normalizeRunStepRecord(input: unknown): RunStepRecord {
  const raw = asRecord(input) ?? {};
  return {
    id: stringValue(raw.id, "step"),
    run_id: stringValue(raw.run_id, ""),
    step_index: numberValue(raw.step_index, 0),
    step_id: stringValue(raw.step_id, ""),
    tool_name: stringValue(raw.tool_name ?? raw.tool, ""),
    status: stringValue(raw.status, ""),
    args: recordValue(raw.args),
    result: recordValue(raw.result),
    error_message: stringOrNull(raw.error_message ?? raw.error),
    started_at: stringOrNull(raw.started_at),
    completed_at: stringOrNull(raw.completed_at),
    duration_ms: numberOrNull(raw.duration_ms),
  };
}

export function normalizePlaybookRunListResponse(payload: unknown): RunListResponse {
  const record = asRecord(payload);
  const data = asRecord(record?.data);
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(record?.runs)
      ? record.runs
      : Array.isArray(data?.runs)
        ? data.runs
        : [];
  return {
    runs: source.map((item) => normalizePlaybookRunRecord(item, { includeResult: false })),
  };
}

export function normalizePlaybookRunDetailResponse(payload: unknown): RunDetailResponse {
  const record = asRecord(payload);
  const data = asRecord(record?.data);
  const run = normalizePlaybookRunRecord(record?.run ?? data?.run ?? payload, { includeResult: true });
  const artifactsSource = record?.artifacts ?? data?.artifacts;
  const artifacts = Array.isArray(artifactsSource)
    ? artifactsSource.map(normalizeArtifactRecord)
    : [];
  const stepsSource = record?.steps ?? data?.steps;
  const steps = Array.isArray(stepsSource) ? stepsSource.map(normalizeRunStepRecord) : [];
  return { run, steps, artifacts };
}

export function normalizePlaybookToSkill(input: unknown): Skill {
  const raw = asRecord(input) ?? {};
  const slug = stringValue(raw.slug, "");
  const id = slug || stringValue(raw.id ?? raw.template_id ?? raw.name, "playbook");
  const tools = arrayOfStrings(raw.tools ?? raw.tools_used);
  const inputPolicy = recordValue(raw.input_policy ?? raw.inputPolicy);
  const producesCode = Boolean(raw.produces_code ?? raw.producesCode);
  return {
    id,
    title: stringValue(raw.name ?? raw.title, "Untitled migration"),
    category: stringValue(raw.category, "playbook"),
    description: stringValue(raw.description ?? raw.goal, "Ready to run against the selected org."),
    verdict: stringValue(raw.verdict, producesCode ? "Better" : "Good"),
    tools_used: tools,
    inputs: buildPlaybookInputs(id, inputPolicy, tools),
    steps: tools.map((tool, index) => ({
      id: `${String(index + 1).padStart(2, "0")}_${tool}`,
      tool,
    })),
    result_template: stringOrNull(raw.result_template) ?? undefined,
    rating: null,
    permissions: producesCode ? ["artifact_write"] : [],
    chain_target: true,
    prompt: stringValue(raw.prompt, ""),
    prompt_version: numberValue(raw.prompt_version, 1),
  };
}

export function normalizePlaybookRunRecord(
  input: unknown,
  opts: { includeResult?: boolean } = {}
): RunSummary {
  const raw = asRecord(input) ?? {};
  const id = stringValue(raw.id ?? raw.run_id, "run");
  return {
    id,
    skill_id: stringValue(raw.skill_id ?? raw.template_slug ?? raw.template_id ?? raw.playbook_id, ""),
    surface_name: stringValue(raw.surface_name ?? raw.template_name ?? raw.playbook_name, ""),
    surface_slug: stringValue(raw.surface_slug ?? raw.template_slug ?? raw.slug, ""),
    surface_category: stringValue(raw.surface_category ?? raw.category, ""),
    target_name: stringOrNull(raw.target_name ?? raw.metadata_name),
    target_type: stringOrNull(raw.target_type ?? raw.metadata_type),
    target_id: stringOrNull(raw.target_id ?? raw.metadata_id),
    inputs: recordValue(raw.inputs ?? raw.inputs_json),
    inputs_hash: stringOrNull(raw.inputs_hash),
    status: stringValue(raw.status, "done") as RunSummary["status"],
    result_md: opts.includeResult ? stringOrNull(raw.result_md ?? raw.markdown) : null,
    error_message: stringOrNull(raw.error_message ?? raw.error),
    user_id: stringOrNull(raw.user_id),
    org_id: stringOrNull(raw.org_id),
    retrieval_id: stringOrNull(raw.retrieval_id),
    started_at: stringValue(raw.started_at ?? raw.created_at, new Date().toISOString()),
    completed_at: stringOrNull(raw.completed_at),
    total_ms: numberOrNull(raw.total_ms),
    cost_usd: numberOrNull(raw.cost_usd),
    source: stringValue(raw.source, "manual"),
    parent_run_id: stringOrNull(raw.parent_run_id),
  };
}

export function buildPlaybookInputs(id: string, inputPolicy: Record<string, unknown>, tools: string[]): SkillInput[] {
  const metadataTypes = arrayOfStrings(inputPolicy.metadata_types ?? inputPolicy.metadataTypes);
  const targetRequired = Boolean(inputPolicy.target_required ?? inputPolicy.targetRequired);
  const questionRequired = Boolean(inputPolicy.question_required ?? inputPolicy.questionRequired);
  const supportsField = Boolean(inputPolicy.field_supported ?? inputPolicy.fieldSupported);
  const supportsExpansive = Boolean(inputPolicy.expansive_supported ?? inputPolicy.expansiveSupported);
  const runAll = Boolean(inputPolicy.run_all ?? inputPolicy.runAll);
  const mode = stringValue(inputPolicy.mode, "org");
  const inputs: SkillInput[] = [];

  if (questionRequired) {
    inputs.push({
      name: "question",
      type: "text",
      label: "Request",
      required: true,
      placeholder: "Describe what you want this migration to produce.",
      help: "The migration uses this request with org metadata to produce a concrete result.",
    });
  }

  if (!targetRequired && runAll) {
    inputs.push({
      name: "run_all",
      type: "boolean",
      label: mode === "metadata" && metadataTypes.length ? `Run all ${metadataTypes.map(humanize).join(" / ")}` : "Run all",
      required: false,
      default: true,
      help: "Runs the migration against the connected org. Optionally select a narrower target below when available.",
    });
  }

  if (targetRequired) {
    inputs.push(...targetInputsForPolicy(metadataTypes, true));
  } else if (mode === "metadata" && metadataTypes.length > 0) {
    inputs.push(...targetInputsForPolicy(metadataTypes, false));
  } else if (questionRequired && tools.includes("get_object_fields")) {
    inputs.push({
      name: "object_name",
      type: "select",
      label: "Object",
      required: false,
      source: "list_objects",
      placeholder: "Optional object context",
      help: "Optional. Leave blank to let the migration infer the object from your request.",
    });
  }

  if (supportsField) {
    inputs.push({
      name: "field_name",
      type: "select",
      label: "Field",
      required: false,
      source: "get_object_fields",
      source_args: { object_name: "{{object_name}}" },
      default: "__all__",
      placeholder: "All fields",
      help: "Optional. Pick a field to focus the field-level references and record analysis.",
    });
  }

  if (id === "object-relationship-360" || supportsExpansive) {
    inputs.push({
      name: "expansive_graph",
      type: "boolean",
      label: "Expansive Graph View",
      required: false,
      default: false,
      help: "When enabled, includes a wider relationship graph around the selected object.",
    });
  }

  return inputs;
}

export function targetInputsForPolicy(metadataTypes: string[], required: boolean): SkillInput[] {
  if (metadataTypes.includes("ApexClass") && metadataTypes.includes("ApexTrigger")) {
    return [
      {
        name: "metadata_type",
        type: "select",
        label: "Apex Type",
        required,
        default: "ApexClass",
        source: "list_metadata_types",
        allowed_values: ["ApexClass", "ApexTrigger"],
        help: "Loaded from the connected org, filtered to Apex classes and triggers.",
      },
      {
        name: "metadata_name",
        type: "select",
        label: "Apex Class or Trigger",
        required,
        source: "list_metadata",
        source_args: { metadata_type: "{{metadata_type}}" },
        placeholder: "Select metadata",
        help: "The selected class or trigger is passed to the tools and prompt as the target artifact.",
      },
    ];
  }

  if (metadataTypes.length > 1) {
    return [
      {
        name: "metadata_type",
        type: "select",
        label: "Metadata Type",
        required,
        default: metadataTypes[0],
        source: "list_metadata_types",
        allowed_values: metadataTypes,
        help: "Loaded from the connected org, filtered to the metadata types this migration supports.",
      },
      {
        name: "metadata_name",
        type: "select",
        label: "Metadata Name",
        required,
        source: "list_metadata",
        source_args: { metadata_type: "{{metadata_type}}" },
        placeholder: "Select metadata",
        help: "The selected metadata is passed to the graph, source, and LLM tools.",
      },
    ];
  }

  const only = metadataTypes[0] ?? "";
  if (only === "CustomObject") {
    return [
      {
        name: "object_name",
        type: "select",
        label: "Object",
        required,
        source: "list_objects",
        placeholder: "Select object",
        help: "The selected object is used for fields, records, relationships, triggers, and graph context.",
      },
    ];
  }
  if (only === "Flow") {
    return [
      {
        name: "metadata_name",
        type: "select",
        label: "Flow",
        required,
        source: "list_flows",
        placeholder: "Select flow",
        help: "The selected Flow is used as the target for impact and dependency analysis.",
      },
    ];
  }
  if (only === "PermissionSet") {
    return [
      {
        name: "metadata_name",
        type: "select",
        label: "Permission Set",
        required,
        source: "list_perm_sets",
        placeholder: "Select permission set",
        help: "The selected permission set is used for access analysis and generated fixes.",
      },
    ];
  }
  if (only === "ApexClass") {
    return [
      {
        name: "class_name",
        type: "select",
        label: "Apex Class",
        required,
        source: "list_classes",
        placeholder: "Select Apex class",
        help: "The selected Apex class is passed to source, graph, and test tools.",
      },
    ];
  }

  if (only) {
    return [
      {
        name: "metadata_name",
        type: "select",
        label: humanize(only),
        required,
        source: "list_metadata",
        source_args: { metadata_type: only },
        placeholder: `Select ${humanize(only)}`,
        help: `Loaded from the connected org's ${humanize(only)} metadata.`,
      },
    ];
  }

  return [
    {
      name: "metadata_type",
      type: "select",
      label: "Metadata Type",
      required,
      source: "list_metadata_types",
      placeholder: "Select metadata type",
      help: "Loaded from the connected org.",
    },
    {
      name: "metadata_name",
      type: "select",
      label: "Target Metadata",
      required,
      source: "list_metadata",
      source_args: { metadata_type: "{{metadata_type}}" },
      placeholder: "Enter metadata API name",
      help: "The selected metadata is passed to the tools and prompt as the target artifact.",
    },
  ];
}